import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Search } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getEvidence } from "@/lib/api";

const GRADE_STYLE = {
  A: "bg-sage text-bone", B: "bg-ink text-bone", C: "bg-clay/80 text-bone", D: "bg-slatem text-bone", X: "bg-clay text-bone",
};
const GOALS = [
  ["all", "All"], ["energy", "Energy"], ["strength", "Strength"], ["heart", "Heart"], ["sleep", "Sleep"],
  ["digestion", "Digestion"], ["weight", "Weight"], ["coverage", "Coverage"],
];
const GRADES = [["all", "Any grade"], ["A", "A — Strong"], ["B", "B — Good"], ["C", "C — Mixed"], ["D", "D — Early"], ["X", "X — Suppressed"]];

export default function Evidence() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [goal, setGoal] = useState("all");
  const [grade, setGrade] = useState("all");

  useEffect(() => {
    getEvidence().then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter((it) => {
      if (goal !== "all" && !it.goals.includes(goal)) return false;
      if (grade !== "all" && it.grade !== grade) return false;
      if (q && !`${it.nutrient} ${it.headline} ${it.summary}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, q, goal, grade]);

  return (
    <main className="min-h-screen pt-28 pb-24 px-4 sm:px-6" data-testid="evidence-page">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-clay mb-4">the evidence hub</p>
          <h1 className="font-serif text-4xl sm:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] max-w-4xl" data-testid="evidence-title">
            Every claim carries a grade. <span className="italic text-sage">Grades carry consequences.</span>
          </h1>
          <p className="mt-5 text-slatem max-w-2xl leading-relaxed">
            Grade A and B shape plans. Grade C is education only. Grade D is research-watch. Grade X is suppressed everywhere — no exceptions.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-col gap-4">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slatem" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a nutrient or topic…" data-testid="evidence-search"
              className="w-full rounded-full border border-ink/20 bg-bone pl-11 pr-4 py-3 text-sm outline-none focus:border-clay transition-colors min-h-[44px]" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="evidence-goal-filters">
            {GOALS.map(([v, l]) => (
              <button key={v} onClick={() => setGoal(v)} data-testid={`evidence-goal-${v}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold border min-h-[44px] transition-colors duration-150 ${goal === v ? "bg-ink text-bone border-ink" : "border-ink/20 hover:border-ink/50"}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="evidence-grade-filters">
            {GRADES.map(([v, l]) => (
              <button key={v} onClick={() => setGrade(v)} data-testid={`evidence-grade-${v}`}
                className={`rounded-full px-4 py-2 text-xs font-mono uppercase tracking-wider border min-h-[44px] transition-colors duration-150 ${grade === v ? "bg-clay text-bone border-clay" : "border-ink/20 hover:border-ink/50"}`}>{l}</button>
            ))}
          </div>
        </div>

        {!items ? (
          <div className="mt-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-clay" /></div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="evidence-grid">
            {filtered.map((it, i) => (
              <motion.article key={it.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 * i }}
                className={`rounded-3xl border p-6 flex flex-col ${it.grade === "X" ? "border-clay/50 bg-clay/5" : "border-ink/12 bg-bone"}`}
                data-testid={`evidence-card-${it.id}`}>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="font-serif text-2xl font-bold uppercase tracking-tight">{it.nutrient}</h2>
                  <span className={`shrink-0 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${GRADE_STYLE[it.grade]}`} data-testid={`evidence-card-${it.id}-grade`}>
                    {it.grade}
                  </span>
                </div>
                <p className="text-sm font-semibold mb-2">{it.headline}</p>
                <p className="text-sm text-slatem leading-relaxed mb-4">{it.summary}</p>
                <p className="text-xs text-ink/70 mb-3"><span className="font-mono uppercase tracking-[0.15em] text-slatem mr-1.5">applies to</span>{it.applicability}</p>
                {it.food_sources.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {it.food_sources.map((f) => (
                      <span key={f} className="rounded-full bg-sand border border-ink/10 px-2.5 py-1 text-[11px] text-ink/70">{f}</span>
                    ))}
                  </div>
                )}
                <div className="mt-auto pt-4 border-t border-ink/10">
                  <p className="text-[11px] text-clay/90 leading-relaxed mb-2">{it.safety_note}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slatem">{it.source} · reviewed {it.review_date}</p>
                </div>
              </motion.article>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-full text-center text-slatem py-16" data-testid="evidence-empty">Nothing matches — try clearing a filter.</p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
